import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Box, Typography, Chip, useTheme, useMediaQuery } from '@mui/material';
import { Timer as TimerIcon, Warning as WarningIcon } from '@mui/icons-material';

interface EmergencyChronometerProps {
  startTime: Date | string;
  size?: 'small' | 'medium' | 'large';
  showWarnings?: boolean;
  variant?: 'chip' | 'text' | 'detailed';
}

const EmergencyChronometer: React.FC<EmergencyChronometerProps> = ({
  startTime,
  size = 'medium',
  showWarnings = true,
  variant = 'chip'
}) => {
  const [elapsedSeconds, setElapsedSeconds] = useState<number>(0);
  const [currentTime, setCurrentTime] = useState<Date>(new Date());
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Optimized timer with requestAnimationFrame for better performance
  const frameRef = useRef<number>();
  const lastUpdateRef = useRef<number>(0);

  const updateTime = useCallback(() => {
    const now = Date.now();
    
    // Only update if at least 1000ms has passed (1 second)
    if (now - lastUpdateRef.current >= 1000) {
      const start = new Date(startTime).getTime();
      const elapsedMs = now - start;
      const totalSeconds = Math.floor(elapsedMs / 1000);
      
      setElapsedSeconds(totalSeconds);
      setCurrentTime(new Date(now));
      lastUpdateRef.current = now;
    }

    // Continue the animation loop
    frameRef.current = requestAnimationFrame(updateTime);
  }, [startTime]);

  useEffect(() => {
    // Initial calculation
    const start = new Date(startTime).getTime();
    const elapsedMs = Date.now() - start;
    const totalSeconds = Math.floor(elapsedMs / 1000);
    setElapsedSeconds(totalSeconds);
    lastUpdateRef.current = Date.now();

    // Start the animation loop
    frameRef.current = requestAnimationFrame(updateTime);

    return () => {
      if (frameRef.current) {
        cancelAnimationFrame(frameRef.current);
      }
    };
  }, [startTime, updateTime]);

  const formatTime = (totalSeconds: number): string => {
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    
    if (hours > 0) {
      return `${hours}h ${minutes.toString().padStart(2, '0')}m ${seconds.toString().padStart(2, '0')}s`;
    }
    return `${minutes}m ${seconds.toString().padStart(2, '0')}s`;
  };

  const getUrgencyLevel = (totalSeconds: number): 'normal' | 'warning' | 'critical' => {
    const minutes = Math.floor(totalSeconds / 60);
    if (minutes <= 10) return 'normal';
    if (minutes <= 20) return 'warning';
    return 'critical';
  };

  const getColor = (urgency: string) => {
    switch (urgency) {
      case 'normal': return 'success';
      case 'warning': return 'warning';
      case 'critical': return 'error';
      default: return 'default';
    }
  };

  // Mobile-responsive sizing
  const getResponsiveSize = () => {
    if (isSmallMobile) {
      return size === 'large' ? 'medium' : size === 'medium' ? 'small' : 'small';
    }
    return size;
  };

  const getResponsiveTextVariant = () => {
    if (isSmallMobile) {
      return size === 'large' ? 'h5' : size === 'medium' ? 'h6' : 'body1';
    }
    if (isMobile) {
      return size === 'large' ? 'h4' : size === 'medium' ? 'h5' : 'body2';
    }
    return size === 'large' ? 'h4' : size === 'medium' ? 'h6' : 'body2';
  };

  const getResponsiveIconSize = () => {
    if (isSmallMobile) {
      return size === 'large' ? 'medium' : 'small';
    }
    return size;
  };

  const urgency = getUrgencyLevel(elapsedSeconds);
  const color = getColor(urgency);
  const responsiveSize = getResponsiveSize();
  const responsiveTextVariant = getResponsiveTextVariant();
  const responsiveIconSize = getResponsiveIconSize();

  if (variant === 'chip') {
    return (
      <Chip
        icon={urgency === 'critical' ? <WarningIcon fontSize={responsiveIconSize} /> : <TimerIcon fontSize={responsiveIconSize} />}
        label={formatTime(elapsedSeconds)}
        color={color as any}
        size={responsiveSize}
        sx={{
          fontWeight: 'bold',
          animation: urgency === 'critical' ? 'pulse 1s infinite' : 'none',
          fontSize: isSmallMobile ? '0.9rem' : isMobile ? '1rem' : undefined,
          minHeight: isMobile ? '36px' : undefined,
          '& .MuiChip-label': {
            px: isSmallMobile ? 1 : undefined,
            fontSize: isSmallMobile ? '0.85rem' : undefined
          },
          '@keyframes pulse': {
            '0%': { opacity: 1 },
            '50%': { opacity: 0.7 },
            '100%': { opacity: 1 }
          }
        }}
      />
    );
  }

  if (variant === 'text') {
    return (
      <Typography
        variant={responsiveTextVariant as any}
        color={`${color}.main`}
        sx={{
          fontWeight: 'bold',
          display: 'flex',
          alignItems: 'center',
          gap: isSmallMobile ? 0.3 : 0.5,
          flexWrap: isSmallMobile ? 'wrap' : 'nowrap',
          justifyContent: isSmallMobile ? 'center' : 'flex-start'
        }}
      >
        <TimerIcon fontSize={responsiveIconSize} />
        {formatTime(elapsedSeconds)}
      </Typography>
    );
  }

  // Detailed variant
  return (
    <Box
      sx={{
        p: isSmallMobile ? 1.5 : 2,
        bgcolor: `${color}.light`,
        borderRadius: 2,
        border: urgency === 'critical' ? `2px solid` : 'none',
        borderColor: `${color}.main`,
        animation: urgency === 'critical' ? 'pulse 2s infinite' : 'none',
        minHeight: isMobile ? 'auto' : undefined
      }}
    >
      <Box sx={{ 
        display: 'flex', 
        alignItems: isSmallMobile ? 'flex-start' : 'center', 
        gap: isSmallMobile ? 0.5 : 1, 
        mb: 1,
        flexDirection: isSmallMobile ? 'column' : 'row',
        textAlign: isSmallMobile ? 'center' : 'left'
      }}>
        {urgency === 'critical' ? (
          <WarningIcon color="error" fontSize={responsiveIconSize === 'large' ? 'large' : 'medium'} />
        ) : (
          <TimerIcon color={color as any} fontSize={responsiveIconSize === 'large' ? 'large' : 'medium'} />
        )}
        <Typography 
          variant={responsiveTextVariant as any} 
          color={`${color}.main`} 
          fontWeight="bold"
          sx={{ 
            fontSize: isSmallMobile ? '1.5rem' : undefined,
            lineHeight: isSmallMobile ? 1.2 : undefined 
          }}
        >
          {formatTime(elapsedSeconds)}
        </Typography>
      </Box>
      
      <Typography 
        variant={isSmallMobile ? 'caption' : 'body2'} 
        color="text.secondary"
        sx={{ textAlign: isSmallMobile ? 'center' : 'left' }}
      >
        Tiempo transcurrido desde la alerta
      </Typography>
      
      {showWarnings && (
        <Box sx={{ mt: 1, textAlign: isSmallMobile ? 'center' : 'left' }}>
          {urgency === 'warning' && (
            <Typography 
              variant={isSmallMobile ? 'caption' : 'caption'} 
              color="warning.main"
              sx={{ fontSize: isSmallMobile ? '0.75rem' : undefined }}
            >
              ⚠️ Tiempo de respuesta elevado
            </Typography>
          )}
          {urgency === 'critical' && (
            <Typography 
              variant={isSmallMobile ? 'caption' : 'caption'} 
              color="error.main" 
              fontWeight="bold"
              sx={{ fontSize: isSmallMobile ? '0.8rem' : undefined }}
            >
              🚨 TIEMPO CRÍTICO - Intervención urgente requerida
            </Typography>
          )}
        </Box>
      )}
      
      <Typography 
        variant="caption" 
        color="text.secondary" 
        sx={{ 
          display: 'block', 
          mt: 1,
          textAlign: isSmallMobile ? 'center' : 'left',
          fontSize: isSmallMobile ? '0.7rem' : undefined
        }}
      >
        Iniciado: {new Date(startTime).toLocaleTimeString('es-CL')}
      </Typography>
    </Box>
  );
};

export default EmergencyChronometer;